import {DateTime} from 'luxon';
import {datePresets, RelativeDatePreset} from "./presets";
import {RelativeDateAnchor, RelativeDateRule, RelativeDateUnit} from "./utils";

export const DEFAULT_DATE_FILTER: DateFilter = {
    preset: 'today',
    start: DateTime.local().startOf('day'),
    end: DateTime.local().endOf('day'),
};

export type DateFilter = {
    preset?: RelativeDatePreset;
    rule?: RelativeDateRule;
    start: DateTime | null;
    end: DateTime | null;
};

export type FilterDateRangeProps = {
    initialValue: DateFilter | null;
    onChange: (dateFilter: DateFilter | null) => void;
    label?: string;
};

export type DraftMode = 'preset' | 'relative' | 'custom';
export type RelativeDirection = 'last' | 'next' | 'current' | 'before' | 'after';
export type AnchorChoice = 'now' | 'date';

export type RelativeDraft = {
    direction: RelativeDirection;
    amount: number;
    unit: RelativeDateUnit;
    anchorChoice: AnchorChoice;
    anchorDate: string;
};

export const MODE_OPTIONS = [
    {value: 'preset', label: 'Preset'},
    {value: 'relative', label: 'Relative Date'},
    {value: 'custom', label: 'Custom Range'},
] as const;

export const DIRECTION_OPTIONS = [
    {value: 'last', label: 'last'},
    {value: 'next', label: 'next'},
    {value: 'current', label: 'current'},
    {value: 'before', label: 'before'},
    {value: 'after', label: 'after'},
] as const;

export const UNIT_OPTIONS = [
    {value: 'day', label: 'days'},
    {value: 'week', label: 'weeks'},
    {value: 'month', label: 'months'},
] as const;

export const ANCHOR_OPTIONS = [
    {value: 'now', label: 'From now'},
    {value: 'date', label: 'From a date'},
] as const;

export const MOST_USED_PRESET_ORDER: RelativeDatePreset[] = [
    'today',
    'yesterday',
    'last 7 days',
    'this week',
    'this month',
];

export function toIso(value: DateTime | null): string {
    return value ? value.toFormat('yyyy-MM-dd') : '';
}

export function fromIso(value: string): DateTime | null {
    if (!value) {
        return null;
    }
    const parsed = DateTime.fromFormat(value, 'yyyy-MM-dd');
    return parsed.isValid ? parsed : null;
}

export function inferMode(initialValue: DateFilter | null): DraftMode {
    if (!initialValue) {
        return 'preset';
    }
    if (initialValue.preset) {
        return 'preset';
    }
    if (initialValue.rule) {
        return 'relative';
    }
    if (initialValue.start && initialValue.end) {
        return 'custom';
    }
    return 'relative';
}


export function buildPresetSelectData() {
    const mostUsed = datePresets.filter((preset) =>
        MOST_USED_PRESET_ORDER.includes(preset.preset),
    );
    const allRemaining = datePresets.filter(
        (preset) => !MOST_USED_PRESET_ORDER.includes(preset.preset),
    );

    return [
        {
            group: 'Most used',
            items: mostUsed.map((preset) => ({
                value: preset.preset,
                label: preset.label,
            })),
        },
        {
            group: 'All presets',
            items: allRemaining.map((preset) => ({
                value: preset.preset,
                label: preset.label,
            })),
        },
    ];
}

export function ruleFromDraft(draft: RelativeDraft): RelativeDateRule | null {
    const anchor: RelativeDateAnchor | undefined =
        draft.anchorChoice === 'now'
            ? {type: 'now'}
            : (() => {
                const anchorDate = fromIso(draft.anchorDate);
                if (!anchorDate) {
                    return undefined;
                }
                return {type: 'date', value: anchorDate} as const;
            })();

    if (!anchor) {
        return null;
    }

    if (draft.direction === 'current') {
        return {
            kind: 'bounded',
            direction: 'current',
            unit: draft.unit,
            anchor,
        };
    }

    if (!Number.isInteger(draft.amount) || draft.amount < 1) {
        return null;
    }

    if (draft.direction === 'before' || draft.direction === 'after') {
        return {
            kind: 'open',
            direction: draft.direction,
            amount: draft.amount,
            unit: draft.unit,
            anchor,
        };
    }

    return {
        kind: 'bounded',
        direction: draft.direction,
        amount: draft.amount,
        unit: draft.unit,
        anchor,
    };
}

export function draftFromRule(rule: RelativeDateRule): RelativeDraft {
    const anchorChoice: AnchorChoice =
        rule.anchor?.type === 'date' ? 'date' : 'now';
    const anchorDate =
        rule.anchor?.type === 'date'
            ? rule.anchor.value.toFormat('yyyy-MM-dd')
            : '';

    if (rule.kind === 'open') {
        return {
            direction: rule.direction,
            amount: rule.amount,
            unit: rule.unit,
            anchorChoice,
            anchorDate,
        };
    }

    return {
        direction: rule.direction,
        amount: rule.amount ?? 1,
        unit: rule.unit,
        anchorChoice,
        anchorDate,
    };
}